import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

export default function CustomCursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // Bail on touch devices
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const dot = dotRef.current;
    const ring = ringRef.current;
    const label = labelRef.current;
    if (!dot || !ring || !label) return;

    // Show cursor elements
    dot.style.opacity = '1';
    ring.style.opacity = '1';

    const quickDotX = gsap.quickTo(dot, 'x', {
      duration: 0.15,
      ease: 'power3.out',
    });
    const quickDotY = gsap.quickTo(dot, 'y', {
      duration: 0.15,
      ease: 'power3.out',
    });
    const quickRingX = gsap.quickTo(ring, 'x', {
      duration: 0.4,
      ease: 'power3.out',
    });
    const quickRingY = gsap.quickTo(ring, 'y', {
      duration: 0.4,
      ease: 'power3.out',
    });

    const onMouseMove = (e: MouseEvent) => {
      quickDotX(e.clientX);
      quickDotY(e.clientY);
      quickRingX(e.clientX);
      quickRingY(e.clientY);
    };

    const onMouseEnterInteractive = (e: MouseEvent) => {
      const target = e.currentTarget as HTMLElement;
      const cursorType = target.dataset.cursor || 'pointer';

      if (cursorType === 'expand') {
        // Expand state for portfolio rows
        gsap.to(ring, { scale: 2.5, opacity: 0.3, duration: 0.3 });
        gsap.to(dot, { scale: 0.5, duration: 0.3 });
        label.textContent = '＋';
        gsap.to(label, { opacity: 1, scale: 1, duration: 0.2 });
      } else {
        // Default interactive hover
        gsap.to(ring, { scale: 1.8, opacity: 0.5, duration: 0.3 });
        gsap.to(dot, { scale: 0.5, duration: 0.3 });
        label.textContent = '';
        gsap.to(label, { opacity: 0, scale: 0.5, duration: 0.15 });
      }
    };

    const onMouseLeaveInteractive = () => {
      gsap.to(ring, { scale: 1, opacity: 0.7, duration: 0.3 });
      gsap.to(dot, { scale: 1, duration: 0.3 });
      gsap.to(label, { opacity: 0, scale: 0.5, duration: 0.15 });
    };

    window.addEventListener('mousemove', onMouseMove);

    // Add hover effect to interactive elements
    const interactiveEls = document.querySelectorAll(
      'a, button, [role="button"], .cursor-pointer, [data-cursor]',
    );
    interactiveEls.forEach((el) => {
      el.addEventListener(
        'mouseenter',
        onMouseEnterInteractive as EventListener,
      );
      el.addEventListener(
        'mouseleave',
        onMouseLeaveInteractive as EventListener,
      );
    });

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      interactiveEls.forEach((el) => {
        el.removeEventListener(
          'mouseenter',
          onMouseEnterInteractive as EventListener,
        );
        el.removeEventListener(
          'mouseleave',
          onMouseLeaveInteractive as EventListener,
        );
      });
    };
  }, []);

  return (
    <>
      {/* Dot */}
      <div
        ref={dotRef}
        className="fixed top-0 left-0 w-2 h-2 rounded-full pointer-events-none z-[9999] -translate-x-1/2 -translate-y-1/2 hidden md:block bg-[hsl(24,38%,40%)] dark:bg-foreground"
        style={{ opacity: 0 }}
      />
      {/* Ring */}
      <div
        ref={ringRef}
        className="fixed top-0 left-0 w-9 h-9 rounded-full border pointer-events-none z-[9999] -translate-x-1/2 -translate-y-1/2 hidden md:block border-[hsl(24,38%,40%)]/70 dark:border-foreground/70"
        style={{ opacity: 0 }}
      >
        {/* Contextual label (shows inside ring for expand state, etc.) */}
        <span
          ref={labelRef}
          className="absolute inset-0 flex items-center justify-center text-xs font-sans font-light text-foreground"
          style={{ opacity: 0, transform: 'scale(0.5)' }}
        />
      </div>
    </>
  );
}
